import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Segmented, Avatar, Tooltip, Tag } from "antd";
import {
  PhysicianPageWrapper,
  CustomButton,
  CustomTitle,
  CustomSubTitle,
} from "../physician_page/styled";
import { PlusOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import CCMCoordinatorDashboard from "./segment_options/CCMCoordinatorDashboard";
import CCMCoordinatorPatients from "./segment_options/CCMCoordinatorPatients";
import CCMCoordinatorMyManager from "./segment_options/CCMCoordinatorMyManager";
import { extractToken } from "../../../utils/localStorage";
import { globalRoles } from "../../../utils/roles";
import AddManagerModal from "./component/AddManagerModal";
import AddCoordinatorModal from "./component/AddCoordinatorModal";
import AddPatientModal from "../patients_page/AddPatientModal";
import { ccmManagerService, ccmCoordinatorService } from "../../../services/user";
import { titleFormat, capitalizeFirstLetter } from "../../../utils/utils";
import { debounce, notify } from "../../../utils/helper";
import DeleteConfirmationModal from "../physician_page/component/DeleteConfirmationModal";

const CCMCoordinatorPage = () => {
  const history = useHistory();
  const location = useLocation();
  const user = extractToken();
  const isCCMCoordinator = user?.role === globalRoles.CCM_COORDINATOR;
  const isCCMManager = user?.role === globalRoles.CCM_MANAGER;
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;

  // Helper to get tab from URL
  function getTabFromUrl() {
    const params = new URLSearchParams(location.search);
    return params.get("tab");
  }

  const [coordinatorPagination, setCoordinatorPagination] = useState({
    pageSize: 25,
    current: 1,
    totalResults: 0,
  });
  const [managerPagination, setManagerPagination] = useState({
    pageSize: 25,
    current: 1,
    totalResults: 0,
  });
  const [selectedTab, setSelectedTab] = useState(isSuperAdmin ? "Dashboard" : "Patients");
  const [isCreateManagerModalOpen, setIsCreateManagerModalOpen] = useState(false);
  const [isCreateCoordinatorModalOpen, setIsCreateCoordinatorModalOpen] = useState(false);
  const [coordinatorData, setCoordinatorData] = useState([]);
  const [managerData, setManagerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCoordinatorEdit, setIsCoordinatorEdit] = useState(false);
  const [isManagerEdit, setIsManagerEdit] = useState(false);
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState("");
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [coordinatorSearchText, setCoordinatorSearchText] = useState("");
  const [managerSearchText, setManagerSearchText] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [coordinatorDataCount, setCoordinatorDataCount] = useState(0);
  const [managerDataCount, setManagerDataCount] = useState(0);
  const [coordinatorSorting, setCoordinatorSorting] = useState("");
  const [managerSorting, setManagerSorting] = useState("");
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [patientListRefreshTrigger, setPatientListRefreshTrigger] = useState(0);
  const [coordinatorFilterTab, setCoordinatorFilterTab] = useState([]);
  const [allCoordinator, setAllCoordinator] = useState([]);
  const [specialtyList, setSpecialtyList] = useState(null);



  const fetchCoordinatorData = async (searchInput = coordinatorSearchText) => {
    try {
      setLoading(true);
      const res = await ccmCoordinatorService.getAll(
        coordinatorPagination?.pageSize,
        coordinatorPagination?.current,
        searchInput,
        coordinatorSorting,
        JSON.stringify(coordinatorFilterTab)
      );
      const processData = coordinatorProcessData(res?.data?.results || []);
      setCoordinatorData(processData);
      setCoordinatorDataCount(res?.data?.totalResults || 0);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching coordinator data:", err);
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!isCCMCoordinator && !isCCMManager){
    fetchCoordinatorData();
    }
  }, [isCCMCoordinator, coordinatorPagination, coordinatorSorting, coordinatorFilterTab]);

  const fetchManagerData = async (searchInput = managerSearchText) => {
    try {
      setLoading(true);
      const res = await ccmManagerService.getAll(
        managerPagination?.pageSize,
        managerPagination?.current,
        searchInput,
        managerSorting,
      );
      const processData2 = managerProcessData(res?.data?.results || []);
      setManagerData(processData2);
      setManagerDataCount(res?.data?.totalResults || 0);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching manager data:", err);
      setLoading(false);
    }
  };
  useEffect(() => {
    if(isCCMCoordinator || user?.role === globalRoles.SUPERADMIN){
      if (selectedTab === "CCM Managers") {
        fetchManagerData();
      }
    }
  }, [selectedTab, managerPagination, managerSorting]);

  useEffect(() => {
    const urlTab = getTabFromUrl();
    if (selectedTab && urlTab !== selectedTab) {
      history.replace({
        pathname: location.pathname,
        search: `?tab=${encodeURIComponent(selectedTab)}`,
      });
    }
  }, [selectedTab]);

  const coordinatorProcessData = (data) => {
    return data.map((item) => ({
      ...item,
      key: item?.id || "",
      name1: item?.name,
      name: (
        <div className="row-flex">
          <div
            className="d-flex justify-content-center"
            style={{ position: "relative" }}
          >
            <Avatar
              size={40}
              style={{
                marginRight: "12px",
                backgroundColor: "var(--border-color)",
                color: "var(--primary-text-color)",
              }}
            >
              {capitalizeFirstLetter(item?.name?.[0])}
            </Avatar>
            {item.isEmailVerified && (
              <CheckCircleFilled
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "8px",
                  color: "#52c41a",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  fontSize: "16px",
                }}
              />
            )}
          </div>
          <div
            className="d-flex flex-column justify-start align-start"
            style={{ gap: "0px" }}
          >
            <span>
              {capitalizeFirstLetter(item.name)}{" "}
              <span className="ml-5">
                {" "}
                {item?.boardMember && (
                  <Tooltip title="Board Member">
                    <span>
                      <Tag className="cursor board-member-tag">BM</Tag>
                    </span>
                  </Tooltip>
                )}
              </span>
            </span>
            <span>{capitalizeFirstLetter(item?.email)}</span>
          </div>
        </div>
      ),
      specialtyName: item?.specialty ? titleFormat(item?.specialty) : "CCM - Chronic Care Management" || "",
    }));
  };

  const managerProcessData = (data) => {
    return data.map((item) => ({
      ...item,
      key: item?.id || "",
      name: (
        <>
          <div className="row-flex">
            <div className="d-flex justify-content-center">
              <Avatar
                size={40}
                style={{
                  marginRight: "8px",
                  backgroundColor: "var(--border-color)",
                  color: "var(--primary-text-color)",
                }}
              >
                {capitalizeFirstLetter(item?.name?.[0])}
              </Avatar>
            </div>
            <div className="d-flex flex-column justify-start align-start">
              <span>{capitalizeFirstLetter(item?.name)}</span>
              <span>{capitalizeFirstLetter(item?.email)}</span>
            </div>
          </div>
        </>
      ),
      specialtyName: titleFormat(item?.specialty) || "",
    }));
  };

  const handleCoordinatorSearch = useCallback(
    debounce((text) => {
      setCoordinatorSearchText(text);
      fetchCoordinatorData(text);
    }, 500),
    []
  );

  const handleManagerSearch = useCallback(
    debounce((text) => {
      setManagerSearchText(text);
      fetchManagerData(text);
    }, 500),
    []
  );

  const deleteCoordinator = async (coordinatorId) => {
    try {
      setLoading(true);
      await ccmCoordinatorService.delete(coordinatorId);
      fetchCoordinatorData();
      setIsDeleteModalOpen(false);
      setSelectedCoordinatorId("");
      notify("success", "Coordinator deleted successfully");
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const deleteManager = async (managerId) => {
    try {
      setLoading(true);
      await ccmManagerService.delete(managerId);
      fetchManagerData();
      setIsDeleteModalOpen(false);
      setSelectedManagerId("");
      notify("success", "Manager deleted successfully");
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleChangeTable = (a, b, c) => {
    const sortColumn = c?.column?.dataIndex;
    const sortOrder = c?.order;

    if (sortColumn && sortOrder) {
      const normalizedOrder = sortOrder === "ascend" ? "asc" : "desc";
      if (selectedTab === "Dashboard") {
        setCoordinatorSorting(`${sortColumn}:${normalizedOrder}`);
      } else if (selectedTab === "CCM Managers") {
        setManagerSorting(`${sortColumn}:${normalizedOrder}`);
      }
    } else {
      setCoordinatorSorting("");
      setManagerSorting("");
    }
  };

  const fetchAllCoordinator = async () => {
    try {
      const res = await ccmCoordinatorService.getAll(0,1,"","","");
      setAllCoordinator(res?.data?.results);
    } catch (error) {}
  };

  useEffect(() => {
    if(!isCCMCoordinator && !isCCMManager){
      fetchAllCoordinator();
    }
  }, []);

  useEffect(() => {
    if (allCoordinator?.length > 0 && specialtyList === null) {
      const tags = new Set();
      allCoordinator?.filter((item) => {
        if (typeof item?.specialty === "string") {
          tags.add(item?.specialty);
        }
      });
      setSpecialtyList([...tags]);
    }
  }, [allCoordinator, coordinatorData]);


  return (
    <>
    {isCreateCoordinatorModalOpen && (
        <AddCoordinatorModal
          isOpen={isCreateCoordinatorModalOpen}
          setIsOpen={setIsCreateCoordinatorModalOpen}
          isEdit={isCoordinatorEdit}
          setIsEdit={setIsCoordinatorEdit}
          selectedCoordinatorId={selectedCoordinatorId}
          setSelectedCoordinatorId={setSelectedCoordinatorId}
          loading={loading}
          setLoading={setLoading}
          fetchCoordinators={fetchCoordinatorData}
        />
    )}
    {isCreateManagerModalOpen && (
        <AddManagerModal
          isOpen={isCreateManagerModalOpen}
          setIsOpen={setIsCreateManagerModalOpen}
          isEdit={isManagerEdit}
          setIsEdit={setIsManagerEdit}
          selectedManagerId={selectedManagerId}
          setSelectedManagerId={setSelectedManagerId}
          loading={loading}
          setLoading={setLoading}
          fetchManagers={fetchManagerData}
          coordinatorList={allCoordinator}
        />
    )}
    {isDeleteModalOpen && (
      <DeleteConfirmationModal 
        isOpen={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        loading={loading}
        onConfiem={() => {
          if (selectedTab === "Dashboard") {
            deleteCoordinator(selectedCoordinatorId);
          } else if (selectedTab === "CCM Managers") {
            deleteManager(selectedManagerId);
          }
        }}
      />
    )}

    <PhysicianPageWrapper style={{ padding: "40px 60px" }}>
      <Row className="custom-header">
        <Col className="header-item">
          <CustomTitle>
            {isCCMCoordinator || isCCMManager 
              ? `Welcome Back, ${user?.name}`
              : "CCM Coordinator"
            }
          </CustomTitle>
          <CustomSubTitle>CCM Coordinator subtitle</CustomSubTitle>
        </Col>
        <Col className="custom-btn">
          {!isCCMCoordinator && !isCCMManager &&
          (user?.role === globalRoles.SUPERADMIN) && selectedTab === "Dashboard" && (
          <CustomButton
            onClick={() => setIsCreateCoordinatorModalOpen(true)}
          >
            <PlusOutlined className="font-18 mt-5" />
            Add CCM Coordinator
          </CustomButton>
          )}
          {selectedTab === "CCM Managers" ? (
          <CustomButton
            onClick={() => setIsCreateManagerModalOpen(true)}
          >
            <PlusOutlined className="font-18 mt-5" />
            Add CCM Manager
          </CustomButton>
          ) : (
            (selectedTab === "Patients" && (
          <CustomButton onClick={() => setIsAddPatientModalOpen(true)}>
            <PlusOutlined className="font-18 mt-5" />
            Add Patient
          </CustomButton>
          ))
          )}
        </Col>
      </Row>

      <Row className="w-100" style={{ padding: "25px 0" }}>
        <Segmented
          className="w-100"
          style={{ backgroundColor: "#f1f5f9" }}
          options={[
            isSuperAdmin && "Dashboard",
            "Patients",
            !isCCMManager && "CCM Managers",
          ].filter(Boolean)}
          block
          value={selectedTab}
          onChange={(value) => {
            setSelectedTab(value);
            setCoordinatorPagination((prev) => ({
              ...prev,
              current: 1,
              pageSize: 25,
            }));
            setManagerPagination((prev) => ({
              ...prev,
              current: 1,
              pageSize: 25,
            }));
            setCoordinatorSearchText("");
            setManagerSearchText("");
            // Update URL (handled by useEffect, but for instant feedback)
            history.replace({
              pathname: location.pathname,
              search: `?tab=${encodeURIComponent(value)}`,
            });
          }}
        />
      </Row>

      {selectedTab === "Dashboard" && (
        <CCMCoordinatorDashboard
          coordinatorData={coordinatorData}
          coordinatorDataCount={coordinatorDataCount}
          setIsCoordinatorEdit={setIsCoordinatorEdit}
          coordinatorId={selectedCoordinatorId}
          setCoordinatorId={setSelectedCoordinatorId}
          setIsCreateCoordinatorModalOpen={setIsCreateCoordinatorModalOpen}
          loading={loading}
          searchText={coordinatorSearchText}
          handleSearch={handleCoordinatorSearch}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          pagination={coordinatorPagination}
          setPagination={setCoordinatorPagination}
          handleChangeTable={handleChangeTable}
          isCCMCoordinator={isCCMCoordinator}
          isCCMManager={isCCMManager}
          fetchCoordinator={fetchCoordinatorData}
          coordinatorFilterTab={coordinatorFilterTab}
          setCoordinatorFilterTab={setCoordinatorFilterTab}
          specialtyList={specialtyList}
        />
      )}
      {selectedTab === "Patients" && (
        <CCMCoordinatorPatients refreshTrigger={patientListRefreshTrigger} physicianList={coordinatorData} />
      )}
      {selectedTab === "CCM Managers" && (
        <CCMCoordinatorMyManager 
          managerData={managerData}
          managerDataCount={managerDataCount}
          setIsManagerEdit={setIsManagerEdit}
          managerId={selectedManagerId}
          setManagerId={setSelectedManagerId}
          setIsCreateManagerModalOpen={setIsCreateManagerModalOpen}
          loading={loading}
          handleManagerSearch={handleManagerSearch}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          pagination={managerPagination}
          setPagination={setManagerPagination}
          handleChangeTable={handleChangeTable}
          fetchManager={fetchManagerData}
        />
      )}

      {isAddPatientModalOpen && (
        <AddPatientModal
          isOpen={isAddPatientModalOpen}
          setOpen={setIsAddPatientModalOpen}
          ccm={true}
          isEdit={false}
          setEdit={() => {}}
          patientId={null}
          fetchPatient={() => {}}
          physicianList={coordinatorData}
          userDetails={user}
          onPatientCreated={(patientId) => {
            setPatientListRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}
    </PhysicianPageWrapper>
    </>
  );
};

export default CCMCoordinatorPage;
